import { ServiceInjector } from '@/service/registry/ServiceInjector'
import { ReservedStorage } from '@/service/order_info/object/ReservedStorage'
import { S4 } from '@/util/functions/S4'
import { StorageKey } from '@/util/functions/StorageKey'
import { OrderInfoApi } from '@/cowboy/order_info/OrderInfoApi'
import { RequestCodeVersion } from '@/service/order_info/object/RequestCodeVersion'
import { HttpClient } from '@/cowboy/http/HttpClient'
import { OrderInfo } from '@/service/order_info/object/OrderInfo'

/**
 * Wrapper around Session storage management for required data.
 * This Service manages the required stateful data needed
 * for an existing Reward Link flow if a refresh occurs.
 * @class StaticRewardLinkService
 */
export class StaticRewardLinkService extends ServiceInjector {
  /** @type RequestCodeVersion */
  _codeVersion
  /** @type OrderInfo */
  _orderInfo
  /** @type boolean */
  _isPrintable

  /** @type boolean */
  _isLoading = true

  constructor () {
    super()
    const reserved = this._fetchReserved()
    if (reserved) {
      this._configure(reserved)
      this._isLoading = false
    }
  }

  /**
   * Object containing RequestCode and UrlVersion
   * @return {RequestCodeVersion}
   */
  get codeVersion () {
    return this._codeVersion
  }

  /**
   * Object containing original order info
   * @return {OrderInfo}
   */
  get orderInfo () {
    return this._orderInfo
  }

  /**
   * @return {boolean}
   */
  get isPrintable () {
    return this._isPrintable
  }

  /**
   * @return {boolean}
   */
  get isLoading () {
    return this._isLoading
  }

  /**
   * Saves reserved data to Session storage.
   * Should only be used in the router gard on navigation
   * to root RL with code.
   * @param {string} requestCode
   * @param {number} urlVersion
   * @param {boolean} isPrintable
   * @return Promise<void>
   */
  async init (requestCode, urlVersion, isPrintable) {
    if (typeof requestCode !== 'undefined' && typeof urlVersion !== 'undefined') {
      const codeVersion = RequestCodeVersion.fromJSON({ requestCode, urlVersion })
      HttpClient.codeVersion = codeVersion
      const orderInfoResponse = await OrderInfoApi.fetchOriginalOrderInformation()
      const reserved = ReservedStorage.fromJSON({
        codeVersion,
        orderInfo: OrderInfo.fromOrderInfoResponseData(orderInfoResponse).toJSON(),
        isPrintable
      })
      this._persistReserved(reserved)
      this._configure(reserved)
      this._isLoading = false
    } else {
      throw new Error('Missing Request Code')
    }
  }

  /**
   * @private
   * Used to set commonly used data on Http requests
   * @param {ReservedStorage} reserved
   */
  _configure (reserved) {
    if (reserved instanceof ReservedStorage) {
      HttpClient.codeVersion = this._codeVersion = reserved.codeVersion
      this._orderInfo = reserved.orderInfo
      HttpClient.platformHash = reserved.orderInfo?.platformHash
      this._isPrintable = reserved.isPrintable
    }
  }

  /**
   * @private
   * Fetches reserved storage item from
   * session storage as a ReservedStorage object
   * using S4.getObject function
   * @returns {ReservedStorage}
   */
  _fetchReserved () {
    return S4.getObject(StorageKey.RESERVED, ReservedStorage)
  }

  /**
   * @private
   * Saves reserved data to session storage.
   * using S4.setItem function
   * @param {ReservedStorage} data
   */
  _persistReserved (data) {
    if (data instanceof ReservedStorage) {
      S4.setObject(StorageKey.RESERVED, data)
    } else {
      throw Error('Reserved data is not an instance of ReservedStorage.')
    }
  }

  static injectedName () {
    return 'StaticRewardLinkService'
  }
}
